import Controller from "@ember/controller";
import { action } from "@ember/object";
import { restartableTask, task, timeout } from "ember-concurrency";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import ENV from "glesys-controlpanel/config/environment";
import { findRecord, saveRecord } from "@warp-drive/legacy/compat/builders";

export default class PrivateNetworksDetailsController extends Controller {
  @service store;
  @service http;
  @service abilities;

  @tracked billing;
  @tracked view = "list";

  @action
  setView(view) {
    this.view = view;
  }

  get privateNetwork() {
    return this.model.privateNetwork;
  }

  get segments() {
    return this.privateNetwork.hasMany("segments").value() ?? [];
  }

  get isolatedSegmentsCount() {
    return this.segments.filter((segment) => !segment.routable).length;
  }

  get routableSegmentsCount() {
    return this.segments.filter((segment) => segment.routable).length;
  }

  get dataCenters() {
    return this.store.peekAll("data-center").filter((dataCenter) => dataCenter.hasPrivateNetworkSupport);
  }

  loadCost = task(async () => {
    if (this.abilities.cannot("access pricing for organization", this.privateNetwork.project.organization)) {
      return;
    }

    this.billing = await this.http.request({
      url: `${ENV.apiBaseUri}/privatenetwork/estimatedcost`,
      method: "POST",
      body: {
        projectkey: this.privateNetwork.project.id,
        privatenetworkid: this.privateNetwork.id,
      },
    });
  });

  createSegmentTask = task(async (properties) => {
    let segment = this.store.createRecord("private-network-segment", {
      privateNetwork: this.privateNetwork,
      networkAdapters: [],
      ...properties,
      platform: properties.platform.toLowerCase(),
    });

    try {
      await this.store.request(saveRecord(segment));
    } catch (error) {
      segment.unloadRecord();
      throw error;
    }
  });

  // istanbul ignore next
  pollStatusLoop = restartableTask(async (privateNetwork) => {
    if (ENV.environment === "test" || !privateNetwork) {
      return;
    }

    while (true) {
      await timeout(privateNetwork.status === "READY" ? 60 * 1000 : 5 * 1000);
      await this.store.request(findRecord("private-network", privateNetwork.id, { reload: true }));
    }
  });

  refreshStatus = async () => {
    await this.store.request(findRecord("private-network", this.privateNetwork.id, { reload: true }));
    this.pollStatusLoop.perform(this.privateNetwork);
    this.loadCost.perform();
  };
}
